import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import {
  Box, Text, Group, Badge, Loader, Center, Stack,
  TextInput, Select, SimpleGrid, Paper, Table,
  ScrollArea, Pagination, ActionIcon, Tooltip, Divider,
} from '@mantine/core'
import {
  IconSearch, IconFilter, IconRefresh, IconX,
  IconCalendar, IconHistory,
} from '@tabler/icons-react'
import SmartDateInput from '../components/SmartDateInput'
import { getChangelog, getChangelogStats } from '../api/client'

// ── Type/Module labels ────────────────────────────────────────────────────────
const MODULE_LABEL: Record<string, { label: string; labelTh: string; color: string }> = {
  ASSET:     { label: 'Asset',     labelTh: 'ทรัพย์สิน',     color: 'blue' },
  HANDOVER:  { label: 'Handover',  labelTh: 'ส่งมอบ',        color: 'teal' },
  BORROW:    { label: 'Borrow',    labelTh: 'การยืม',        color: 'cyan' },
  REPAIR:    { label: 'Repair',    labelTh: 'ซ่อมบำรุง',     color: 'orange' },
  TRANSFER:  { label: 'Transfer',  labelTh: 'โอนย้าย',       color: 'indigo' },
  LICENSE:   { label: 'License',   labelTh: 'ลิขสิทธิ์',     color: 'violet' },
  CONTRACT:  { label: 'Contract',  labelTh: 'สัญญา',         color: 'grape' },
  USER:      { label: 'User',      labelTh: 'ผู้ใช้งาน',     color: 'pink' },
  INVENTORY: { label: 'Inventory', labelTh: 'วัสดุ',         color: 'lime' },
  SYSTEM:    { label: 'System',    labelTh: 'ระบบ',          color: 'gray' },
}

const TYPE_LABEL: Record<string, { label: string; labelTh: string; color: string }> = {
  ASSET_CREATED:       { label: 'Created',            labelTh: 'สร้างใหม่',           color: 'teal' },
  ASSET_EDIT:          { label: 'Edited',              labelTh: 'แก้ไข',               color: 'blue' },
  ASSET_DELETED:       { label: 'Deleted',             labelTh: 'ลบ',                  color: 'red' },
  ASSET_SWAP:          { label: 'Swapped',             labelTh: 'เปลี่ยนทดแทน',       color: 'orange' },
  STATUS_CHANGE:       { label: 'Status Changed',      labelTh: 'เปลี่ยนสถานะ',       color: 'yellow' },
  LOCATION_CHANGE:     { label: 'Location Changed',    labelTh: 'ย้ายที่ตั้ง',         color: 'cyan' },
  LOCATION_MOVE:       { label: 'Moved',               labelTh: 'ย้าย',               color: 'cyan' },
  OWNER_CHANGE:        { label: 'Owner Changed',       labelTh: 'เปลี่ยนเจ้าของ',     color: 'violet' },
  DEPT_TRANSFER:       { label: 'Dept. Transfer',      labelTh: 'โอนแผนก',            color: 'indigo' },
  HANDOVER:            { label: 'Handover',            labelTh: 'ส่งมอบ',              color: 'teal' },
  HANDOVER_RETURN:     { label: 'Returned',            labelTh: 'คืนจากการส่งมอบ',    color: 'blue' },
  BORROW:              { label: 'Borrowed',            labelTh: 'ยืม',                color: 'cyan' },
  BORROW_APPROVED:     { label: 'Borrow Approved',     labelTh: 'อนุมัติการยืม',      color: 'teal' },
  BORROW_REJECTED:     { label: 'Borrow Rejected',     labelTh: 'ปฏิเสธการยืม',      color: 'red' },
  RETURN:              { label: 'Returned',            labelTh: 'คืน',                color: 'teal' },
  REPAIR_OPENED:       { label: 'Repair Opened',       labelTh: 'เปิดงานซ่อม',        color: 'orange' },
  REPAIR_DONE:         { label: 'Repair Done',         labelTh: 'ซ่อมเสร็จ',          color: 'teal' },
  WRITTEN_OFF:         { label: 'Written Off',         labelTh: 'ตัดจำหน่าย',         color: 'red' },
  DAMAGED:             { label: 'Reported Damaged',    labelTh: 'แจ้งเสียหาย',        color: 'red' },
  TRANSFER_REQUESTED:  { label: 'Transfer Requested',  labelTh: 'ขอโอนย้าย',          color: 'indigo' },
  TRANSFER_APPROVED:   { label: 'Transfer Approved',   labelTh: 'อนุมัติโอนย้าย',     color: 'teal' },
  TRANSFER_REJECTED:   { label: 'Transfer Rejected',   labelTh: 'ปฏิเสธโอนย้าย',     color: 'red' },
  LICENSE_CREATED:     { label: 'License Created',     labelTh: 'สร้างลิขสิทธิ์',    color: 'violet' },
  LICENSE_ASSIGNED:    { label: 'License Assigned',    labelTh: 'มอบหมายลิขสิทธิ์',  color: 'violet' },
  LICENSE_EXPIRED:     { label: 'License Expired',     labelTh: 'หมดอายุ',            color: 'red' },
  LICENSE_RENEWED:     { label: 'License Renewed',     labelTh: 'ต่ออายุลิขสิทธิ์',  color: 'teal' },
  LICENSE_DEACTIVATED: { label: 'License Deactivated', labelTh: 'ปิดใช้งาน',          color: 'gray' },
  CONTRACT_CREATED:    { label: 'Contract Created',    labelTh: 'สร้างสัญญา',         color: 'grape' },
  USER_CREATED:        { label: 'User Created',        labelTh: 'สร้างผู้ใช้',         color: 'pink' },
  USER_UPDATED:        { label: 'User Updated',        labelTh: 'แก้ไขผู้ใช้',         color: 'pink' },
  USER_DELETED:        { label: 'User Deleted',        labelTh: 'ลบผู้ใช้',            color: 'red' },
  USER_LOGIN:          { label: 'Login',               labelTh: 'เข้าสู่ระบบ',         color: 'gray' },
  USER_LOGOUT:         { label: 'Logout',              labelTh: 'ออกจากระบบ',          color: 'gray' },
}

const fmtDate = (d: string) => new Date(d).toLocaleString('th-TH', {
  day: '2-digit', month: 'short', year: '2-digit',
  hour: '2-digit', minute: '2-digit',
})

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ChangeLogPage() {
  const { t, i18n } = useTranslation()
  const { roles }   = useAuth()
  const navigate    = useNavigate()
  const isTh = i18n.language === 'th'

  // Only Admin and Asset_Manager can access
  useEffect(() => {
    if (!roles.includes('ADMIN') && !roles.includes('ASSET_MANAGER')) {
      navigate('/', { replace: true })
    }
  }, [roles])

  const [logs, setLogs]       = useState<any[]>([])
  const [total, setTotal]     = useState(0)
  const [totalPages, setTP]   = useState(1)
  const [loading, setLoading] = useState(false)
  const [stats, setStats]     = useState<any[]>([])

  // Filters
  const [search,     setSearch]     = useState('')
  const [module,     setModule]     = useState<string | null>(null)
  const [type,       setType]       = useState<string | null>(null)
  const [performedBy,setPerformedBy] = useState('')
  const [assetCode,  setAssetCode]  = useState('')
  const [dateFrom,   setDateFrom]   = useState('')
  const [dateTo,     setDateTo]     = useState('')
  const [page,       setPage]       = useState(1)
  const LIMIT = 50

  const load = useCallback(async (p = page) => {
    setLoading(true)
    try {
      const params: any = { page: p, limit: LIMIT }
      if (search)      params.search = search
      if (module)      params.module = module
      if (type)        params.type   = type
      if (performedBy) params.performedBy = performedBy
      if (assetCode)   params.assetCode   = assetCode
      if (dateFrom)    params.dateFrom = dateFrom
      if (dateTo)      params.dateTo   = dateTo

      const res = await getChangelog(params)
      setLogs(res.data.data ?? [])
      setTotal(res.data.total ?? 0)
      setTP(res.data.totalPages ?? 1)
    } catch { setLogs([]) }
    finally { setLoading(false) }
  }, [search, module, type, performedBy, assetCode, dateFrom, dateTo, page])

  const loadStats = async () => {
    try {
      const res = await getChangelogStats(30)
      setStats(res.data ?? [])
    } catch {}
  }

  useEffect(() => { load(1); setPage(1) },
    [search, module, type, performedBy, assetCode, dateFrom, dateTo])
  useEffect(() => { load(page) }, [page])
  useEffect(() => { loadStats() }, [])

  const clearFilters = () => {
    setSearch(''); setModule(null); setType(null)
    setPerformedBy(''); setAssetCode('')
    setDateFrom(''); setDateTo(''); setPage(1)
  }
  const hasFilter = !!(search || module || type || performedBy || assetCode || dateFrom || dateTo)

  // Stats grouped by module
  const statsByModule = Object.entries(MODULE_LABEL).map(([mod, meta]) => ({
    module: mod,
    ...meta,
    count: stats.filter(s => s.module === mod).reduce((sum: number, s: any) => sum + Number(s.count), 0),
  })).filter(s => s.count > 0)

  // Type options filtered by selected module
  const typeOptions = Object.entries(TYPE_LABEL)
    .map(([value, meta]) => ({
      value,
      label: isTh ? meta.labelTh : meta.label,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const moduleOptions = Object.entries(MODULE_LABEL).map(([value, meta]) => ({
    value,
    label: isTh ? meta.labelTh : meta.label,
  }))

  return (
    <Box p="md">
      {/* Header */}
      <Group justify="space-between" mb="xl" align="flex-start" wrap="wrap" gap="sm">
        <Box>
          <Group gap={8}>
            <IconHistory size={22} color="#6366f1" />
            <Text style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600 }}>
              {isTh ? 'บันทึกการเปลี่ยนแปลง' : 'Change Log'}
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            {isTh
              ? 'ติดตามทุกการเปลี่ยนแปลงในระบบ — ทรัพย์สิน, การยืม, ซ่อม, โอนย้าย, ลิขสิทธิ์, ผู้ใช้งาน'
              : 'Track every change across the system — assets, borrows, repairs, transfers, licenses, users'}
          </Text>
        </Box>
        <Tooltip label={isTh ? 'รีเฟรช' : 'Refresh'}>
          <ActionIcon variant="subtle" size="lg" onClick={() => { load(page); loadStats() }}>
            <IconRefresh size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* Stats cards — last 30 days */}
      {statsByModule.length > 0 && (
        <SimpleGrid cols={{ base: 3, md: 6 }} mb="xl">
          {statsByModule.map(s => (
            <Paper key={s.module} withBorder radius="md" p="sm"
              style={{
                cursor: 'pointer',
                borderLeft: `3px solid var(--mantine-color-${s.color}-6)`,
                background: module === s.module ? `var(--mantine-color-${s.color}-0)` : undefined,
              }}
              onClick={() => setModule(prev => prev === s.module ? null : s.module)}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={2}>
                {isTh ? s.labelTh : s.label}
              </Text>
              <Text fw={700} size="xl" c={`${s.color}.7`}>{s.count.toLocaleString()}</Text>
              <Text size="xs" c="dimmed">{isTh ? '30 วันล่าสุด' : 'last 30 days'}</Text>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      {/* Filters */}
      <Paper withBorder radius="md" p="md" mb="lg"
        style={{ borderColor: hasFilter ? '#6366f1' : '#e2e8f0', background: hasFilter ? '#f5f3ff' : undefined }}>
        <Group gap={8} mb={8} wrap="wrap">
          <IconFilter size={16} color={hasFilter ? '#6366f1' : '#94a3b8'} />
          <Text size="sm" fw={600} c={hasFilter ? 'violet' : 'dimmed'}>
            {isTh ? 'กรองข้อมูล' : 'Filters'}
          </Text>
          {hasFilter && (
            <ActionIcon size="xs" variant="subtle" color="gray" onClick={clearFilters}>
              <IconX size={12} />
            </ActionIcon>
          )}
        </Group>
        <Group gap={8} wrap="wrap" align="flex-end">
          <TextInput
            label={isTh ? 'ค้นหา' : 'Search'}
            placeholder={isTh ? 'รหัส, ชื่อ, หมายเหตุ, ผู้ดำเนินการ...' : 'Asset code, note, user...'}
            value={search} onChange={e => setSearch(e.target.value)}
            leftSection={<IconSearch size={14} />} size="xs" style={{ width: 220 }}
            rightSection={search ? <Box style={{ cursor: 'pointer' }} onClick={() => setSearch('')}><IconX size={12} /></Box> : null}
          />
          <Select label={isTh ? 'Module' : 'Module'} data={moduleOptions}
            value={module} onChange={setModule} clearable size="xs" style={{ width: 160 }}
            placeholder={isTh ? 'ทุก Module' : 'All modules'} />
          <Select label={isTh ? 'ประเภทการเปลี่ยนแปลง' : 'Action Type'} data={typeOptions}
            value={type} onChange={setType} clearable searchable size="xs" style={{ width: 200 }}
            placeholder={isTh ? 'ทุกประเภท' : 'All types'} />
          <TextInput label={isTh ? 'ผู้ดำเนินการ' : 'By User'}
            value={performedBy} onChange={e => setPerformedBy(e.target.value)}
            size="xs" style={{ width: 140 }} placeholder="username" />
          <TextInput label={isTh ? 'รหัสทรัพย์สิน' : 'Asset Code'}
            value={assetCode} onChange={e => setAssetCode(e.target.value)}
            size="xs" style={{ width: 140 }} placeholder="IT-TV-0001" />
          <SmartDateInput label={isTh ? 'ตั้งแต่' : 'From'} value={dateFrom}
            onChange={setDateFrom} clearable />
          <SmartDateInput label={isTh ? 'ถึง' : 'To'} value={dateTo}
            onChange={setDateTo} clearable />
        </Group>
      </Paper>

      {/* Result count */}
      <Group justify="space-between" mb="sm">
        <Text size="xs" c="dimmed">
          {isTh ? `พบ ${total.toLocaleString()} รายการ` : `${total.toLocaleString()} records`}
          {hasFilter && ` (${isTh ? 'กรองแล้ว' : 'filtered'})`}
        </Text>
        <Text size="xs" c="dimmed">
          {isTh ? `หน้า ${page} / ${totalPages}` : `Page ${page} of ${totalPages}`}
        </Text>
      </Group>

      {/* Table */}
      {loading ? (
        <Center p="xl"><Loader color="violet" /></Center>
      ) : logs.length === 0 ? (
        <Center p="xl">
          <Stack align="center" gap={4}>
            <IconHistory size={40} color="#cbd5e1" />
            <Text c="dimmed" size="sm">{isTh ? 'ไม่พบรายการ' : 'No records found'}</Text>
          </Stack>
        </Center>
      ) : (
        <ScrollArea>
          <Table highlightOnHover style={{ fontSize: 13 }}>
            <Table.Thead style={{ background: '#f8fafc' }}>
              <Table.Tr>
                {[
                  isTh ? 'วันที่/เวลา' : 'Date/Time',
                  'Module',
                  isTh ? 'ประเภท' : 'Action',
                  isTh ? 'รหัสทรัพย์สิน' : 'Asset Code',
                  isTh ? 'ก่อน' : 'Before',
                  isTh ? 'หลัง' : 'After',
                  isTh ? 'ผู้ดำเนินการ' : 'By',
                  isTh ? 'หมายเหตุ' : 'Note',
                ].map(h => (
                  <Table.Th key={h} style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {logs.map(log => {
                const mod  = MODULE_LABEL[log.module] ?? { label: log.module, labelTh: log.module, color: 'gray' }
                const typ  = TYPE_LABEL[log.type]   ?? { label: log.type,   labelTh: log.type,   color: 'gray' }
                const code = log.assetCode ?? log.asset?.assetCode
                return (
                  <Table.Tr key={log.id}>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      <Text size="xs" c="dimmed">{fmtDate(log.createdAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={mod.color} variant="light">
                        {isTh ? mod.labelTh : mod.label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={typ.color} variant="dot">
                        {isTh ? typ.labelTh : typ.label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {code ? <Text size="xs" fw={600} c="blue">{code}</Text> : <Text size="xs" c="dimmed">—</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed" lineClamp={1} style={{ maxWidth: 120 }}>
                        {log.fromValue ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="teal" lineClamp={1} style={{ maxWidth: 120 }}>
                        {log.toValue ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" fw={500}>{log.performedBy ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed" lineClamp={2} style={{ maxWidth: 240 }}>
                        {log.note ?? '—'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center" mt="lg">
          <Pagination value={page} onChange={setPage} total={totalPages} size="sm" color="violet" />
        </Group>
      )}
    </Box>
  )
}
